import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { ArtReproduction, SizePrice, Tag } from '../../types';
import { API_BASE } from '../../services/api';
import api from '../../services/api';
import SaleBadge from '../SaleBadge';
import './ArtworkModal.css';

interface ExtendedArtReproduction extends ArtReproduction {
  sizePrices?: SizePrice[];
  tags?: Tag[];
}

interface SelectedSize {
  size: string;
  price: number;
  isOriginal?: boolean;
}

const MATERIALS = ['Matte', 'Glossy', 'Canvas', 'Metal'];
const FRAME_OPTIONS = ['None', 'Standard Frame', 'Premium Frame'];
const HANGING_OPTIONS = ['None', 'Standard Hanging', 'Premium Hanging'];

interface ArtworkModalProps {
  isOpen: boolean;
  artwork: ExtendedArtReproduction | null;
  selectedSize?: SelectedSize;
  onSizeSelect: (artworkId: number, sizePrice: SelectedSize) => void;
  onAddToCart: (artwork: ExtendedArtReproduction, options?: { material?: string; frame?: string; hanging?: string; price?: number }) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

const ArtworkModal: React.FC<ArtworkModalProps> = ({
  isOpen,
  artwork,
  selectedSize,
  onSizeSelect,
  onAddToCart,
  onClose,
  isSubmitting = false
}) => {
  const [selectedMaterial, setSelectedMaterial] = useState<string>(MATERIALS[0]);
  const [selectedFrame, setSelectedFrame] = useState<string>(FRAME_OPTIONS[0]);
  const [selectedHanging, setSelectedHanging] = useState<string>(HANGING_OPTIONS[0]);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [calculatingPrice, setCalculatingPrice] = useState<boolean>(false);

  // Calculate price when options change
  useEffect(() => {
    if (!artwork || !selectedSize) {
      setCalculatedPrice(null);
      return;
    }

    const calculatePrice = async (): Promise<void> => {
      setCalculatingPrice(true);
      try {
        const response = await api.post<number>(`/art/${artwork.id}/calculate-price`, {
          basePrice: selectedSize.price,
          size: selectedSize.size,
          material: selectedMaterial,
          frame: selectedFrame !== 'None' ? selectedFrame : null,
          hanging: selectedHanging !== 'None' ? selectedHanging : null,
          widthInches: artwork.sizePrices?.find(sp => sp.size === selectedSize.size)?.width,
          heightInches: artwork.sizePrices?.find(sp => sp.size === selectedSize.size)?.height
        });
        setCalculatedPrice(response.data);
      } catch (error) {
        console.error('Failed to calculate price:', error);
        setCalculatedPrice(selectedSize.price);
      } finally {
        setCalculatingPrice(false);
      }
    };

    calculatePrice();
  }, [artwork, selectedSize, selectedMaterial, selectedFrame, selectedHanging]);

  if (!artwork) return null;

  // Always use the full image URL, not the thumbnail, for the modal
  // Backend stores full image in imageUrl and generates thumbnail separately
  // If imageUrl accidentally contains thumbnail suffix, remove it
  let fullImageUrl = artwork.imageUrl;
  
  // Remove thumbnail suffix if present (backend uses "_thumb" before file extension)
  // Example: "image_thumb.jpg" -> "image.jpg"
  if (fullImageUrl.includes('_thumb.')) {
    fullImageUrl = fullImageUrl.replace('_thumb.', '.');
  } else if (fullImageUrl.includes('_thumb')) {
    // Handle case where _thumb is not before extension
    fullImageUrl = fullImageUrl.replace('_thumb', '');
  }
  
  // Remove thumbnail directory paths
  if (fullImageUrl.includes('/thumbnails/')) {
    fullImageUrl = fullImageUrl.replace('/thumbnails/', '/');
  }
  
  // Construct full URL if needed
  if (!fullImageUrl.startsWith('http')) {
    fullImageUrl = `${API_BASE}${fullImageUrl}`;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={artwork.title} size="extra-large">
      <div className="artwork-modal">
        <div className="artwork-modal__image">
          <img
            src={fullImageUrl}
            alt={artwork.title}
            draggable={false}
            onContextMenu={(event) => event.preventDefault()}
            onDragStart={(event) => event.preventDefault()}
            onError={(event) => {
              // Fallback to thumbnail if full image fails to load
              const target = event.currentTarget;
              if (artwork.thumbnailUrl && target.src !== artwork.thumbnailUrl) {
                const thumbnailUrl = artwork.thumbnailUrl.startsWith('http')
                  ? artwork.thumbnailUrl
                  : `${API_BASE}${artwork.thumbnailUrl}`;
                target.src = thumbnailUrl;
              }
            }}
          />
        </div>
        <div className="artwork-modal__info">
          <div className="artwork-modal__header">
            <h2 className="artwork-modal__title">
              {artwork.title}
              {artwork.activeSale && <SaleBadge sale={artwork.activeSale} className="sale-badge--inline" />}
            </h2>
            {artwork.artistName && artwork.artistId ? (
              <p className="artist-name">
                by <a href={`/artists/${artwork.artistId}`}>{artwork.artistName}</a>
              </p>
            ) : (
              artwork.artistName && <p className="artist-name">by {artwork.artistName}</p>
            )}
          </div>

          {artwork.description && (
            <p className="artwork-description">{artwork.description}</p>
          )}

          {artwork.tags && artwork.tags.length > 0 && (
            <div className="artwork-tags">
              {artwork.tags.map((tag) => (
                <span key={tag.id} className="tag-badge">
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          {artwork.sizePrices && artwork.sizePrices.length > 0 ? (
            <div className="artwork-sizes">
              <label htmlFor={`size-select-${artwork.id}`} className="size-select-label">
                Select Size:
              </label>
              <select
                id={`size-select-${artwork.id}`}
                value={selectedSize?.size || ''}
                onChange={(e) => {
                  const selected = artwork.sizePrices!.find(sp => sp.size === e.target.value);
                  if (selected) {
                    onSizeSelect(artwork.id, {
                      size: selected.size,
                      price: selected.price,
                      isOriginal: selected.isOriginal
                    });
                  }
                }}
                className="size-select"
              >
                <option value="">Choose a size...</option>
                {artwork.sizePrices.map((sp, index) => (
                  <option
                    key={index}
                    value={sp.size}
                    disabled={sp.isOriginal && !artwork.isOriginalAvailable}
                  >
                    {sp.size}
                    {sp.width && sp.height ? ` (${sp.width}" × ${sp.height}")` : ''}
                    {sp.isOriginal ? ' ⭐ Original' : ''}
                    {sp.isOriginal && !artwork.isOriginalAvailable ? ' - SOLD OUT' : ''}
                    {' - $' + sp.price.toFixed(2)}
                  </option>
                ))}
              </select>

              {selectedSize && (
                <>
                  <div className="artwork-options">
                    <div className="form-group">
                      <label htmlFor={`material-select-${artwork.id}`}>Material:</label>
                      <select
                        id={`material-select-${artwork.id}`}
                        value={selectedMaterial}
                        onChange={(e) => setSelectedMaterial(e.target.value)}
                        className="option-select"
                      >
                        {MATERIALS.map((material) => (
                          <option key={material} value={material}>
                            {material}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor={`frame-select-${artwork.id}`}>Frame:</label>
                      <select
                        id={`frame-select-${artwork.id}`}
                        value={selectedFrame}
                        onChange={(e) => setSelectedFrame(e.target.value)}
                        className="option-select"
                      >
                        {FRAME_OPTIONS.map((frame) => (
                          <option key={frame} value={frame}>
                            {frame}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor={`hanging-select-${artwork.id}`}>Hanging:</label>
                      <select
                        id={`hanging-select-${artwork.id}`}
                        value={selectedHanging}
                        onChange={(e) => setSelectedHanging(e.target.value)}
                        className="option-select"
                      >
                        {HANGING_OPTIONS.map((hanging) => (
                          <option key={hanging} value={hanging}>
                            {hanging}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="selected-size-info">
                    <div className="price-breakdown">
                      {artwork.originalPrice && artwork.salePrice && (
                        <div className="price-line price-original">
                          <span>Original Price:</span>
                          <span style={{ textDecoration: 'line-through', color: 'var(--text-secondary)' }}>
                            ${artwork.originalPrice.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="price-line">
                        <span>Base Price:</span>
                        <span>${selectedSize.price.toFixed(2)}</span>
                      </div>
                      {calculatedPrice !== null && calculatedPrice !== selectedSize.price && (
                        <>
                          {artwork.activeSale && (
                            <div className="price-line price-discount">
                              <span>Sale Discount:</span>
                              <span style={{ color: 'var(--color-salmon)' }}>
                                -${(selectedSize.price - (artwork.salePrice || selectedSize.price)).toFixed(2)}
                              </span>
                            </div>
                          )}
                          <div className="price-line price-total">
                            <span><strong>Total:</strong></span>
                            <span><strong>${calculatedPrice.toFixed(2)}</strong></span>
                          </div>
                        </>
                      )}
                      {calculatedPrice === null && !calculatingPrice && (
                        <div className="price-line price-total">
                          <span><strong>Total:</strong></span>
                          <span><strong>${(artwork.salePrice || selectedSize.price).toFixed(2)}</strong></span>
                          {artwork.salePrice && artwork.originalPrice && (
                            <span style={{ textDecoration: 'line-through', color: 'var(--text-secondary)', marginLeft: '8px', fontSize: '0.9em' }}>
                              ${artwork.originalPrice.toFixed(2)}
                            </span>
                          )}
                        </div>
                      )}
                      {calculatingPrice && (
                        <div className="price-line">
                          <span>Calculating...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={() => {
                  if (selectedSize) {
                    onAddToCart(artwork, {
                      material: selectedMaterial,
                      frame: selectedFrame !== 'None' ? selectedFrame : undefined,
                      hanging: selectedHanging !== 'None' ? selectedHanging : undefined,
                      price: calculatedPrice ?? selectedSize.price
                    });
                  }
                }}
                className="btn btn-primary btn-small"
                disabled={!selectedSize || isSubmitting || calculatingPrice}
                style={{ marginTop: 'var(--spacing-sm)', width: '100%' }}
              >
                {isSubmitting && <span className="btn-spinner" aria-hidden="true"></span>}
                {isSubmitting ? 'Adding...' : 'Add to Cart'}
              </button>
            </div>
          ) : (
            <div className="artwork-footer">
              <span className="artwork-price">${artwork.price.toFixed(2)}</span>
              <button
                onClick={() => onAddToCart(artwork)}
                className="btn btn-primary btn-small"
                disabled={isSubmitting}
              >
                {isSubmitting && <span className="btn-spinner" aria-hidden="true"></span>}
                {isSubmitting ? 'Adding...' : 'Add to Cart'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ArtworkModal;
